import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatProgressBar } from '@angular/material/progress-bar';

import { TerraIconModule } from '@ninety/terra';
import { ButtonComponent, ClockTimerComponent } from '@ninety/ui/core';

/** How close to the end of a section the big countdown overlay appears. */
const SECTION_COUNTDOWN_THRESHOLD_SECONDS = 10;

/**
 * The presenter timer values, grouped because they all tick together every second — bundling them
 * into one input costs nothing reactively (they co-change) and keeps the header's contract to five
 * inputs instead of nine. Values that change independently (isPresenter, isPaused, …) stay separate.
 */
export interface MeetingHeaderTimers {
  /** Whole-meeting elapsed seconds. */
  meetingTime: number;
  /** Current-section elapsed seconds. */
  sectionTime: number;
  /** Current-section allotted seconds. */
  sectionDuration: number;
  /** 0–100 for the progress bar. */
  progressBarValue: number;
  /** Router has settled on a section — gates the section clock. */
  navigationEnded: boolean;
}

const EMPTY_TIMERS: MeetingHeaderTimers = {
  meetingTime: 0,
  sectionTime: 0,
  sectionDuration: 0,
  progressBarValue: 0,
  navigationEnded: false,
};

/**
 * Presentational header for {@link MeetingSidenavV2Component}. Renders the presenter band (total +
 * section timers, progress, meeting controls) or, for a non-presenter, the Leave button. Pure
 * inputs → outputs: no store, no services, no side effects — the container owns all of that.
 *
 * The display derivations (`sectionSecondsRemaining`, `isSectionOvertime`, `showCountdown`) live
 * here because they're pure functions of the timer inputs; the container passes raw values.
 */
@Component({
  standalone: true,
  selector: 'ninety-meeting-sidenav-header',
  templateUrl: './meeting-sidenav-header.component.html',
  styleUrls: ['./meeting-sidenav-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressBar, ClockTimerComponent, ButtonComponent, TerraIconModule],
})
export class MeetingSidenavHeaderComponent {
  /** Presenter sees timers + controls; everyone else sees Leave. */
  readonly isPresenter = input(false);
  /** Label for the Leave button, e.g. "Meeting" → "Leave Meeting". Non-presenter only. */
  readonly meetingItemLabel = input('');

  /** Presenter timer values — see {@link MeetingHeaderTimers}. */
  readonly timers = input(EMPTY_TIMERS);

  readonly isPaused = input(false);
  readonly suspending = input(false);

  readonly pauseToggled = output<void>();
  /** `true` skips to the next section, `false` to the previous. */
  readonly sectionSkipped = output<boolean>();
  readonly suspended = output<void>();
  /** A non-presenter clicked Leave. */
  readonly attendeeLeft = output<void>();

  protected readonly sectionSecondsRemaining = computed(() => {
    const { sectionDuration, sectionTime } = this.timers();
    return sectionDuration - sectionTime;
  });
  protected readonly isSectionOvertime = computed(() => this.sectionSecondsRemaining() < 0);
  protected readonly showCountdown = computed(
    () => !this.isSectionOvertime() && this.sectionSecondsRemaining() <= SECTION_COUNTDOWN_THRESHOLD_SECONDS
  );
}
